/**
 * 会员管理系统 - 价格方案管理 API
 *
 * 提供价格方案的 CRUD 操作
 */

import httpRequest from '@/utils/api'
import type {
  PlanPricing,
  CreatePricingRequest,
  UpdatePricingRequest,
  PricingListResponseData,
  PricingDetailResponseData,
  DeletePricingResponseData,
  CheckPurchaseLimitResponseData,
} from '@/types/membership/pricing'

enum API {
  pricings = '/admin/pricings',
  planPricings = '/admin/plans',
}

/**
 * 获取套餐的所有价格方案
 *
 * @param planId 套餐 ID
 * @param includeHidden 是否包含隐藏的价格方案，默认 false
 * @returns 价格方案列表响应
 */
export const getPlanPricingsAPI = (planId: number, includeHidden: boolean = false) => {
  return httpRequest.get<PricingListResponseData>({
    url: `${API.planPricings}/${planId}/pricings`,
    params: { include_hidden: includeHidden },
  })
}

/**
 * 根据 ID 获取价格方案
 *
 * @param pricingId 价格方案 ID
 * @returns 价格方案详情响应
 */
export const getPricingByIdAPI = (pricingId: number) => {
  return httpRequest.get<PricingDetailResponseData>({
    url: `${API.pricings}/${pricingId}`,
  })
}

/**
 * 创建价格方案
 *
 * @param planId 套餐 ID
 * @param data 创建价格方案请求数据
 * @returns 价格方案详情响应
 */
export const createPricingAPI = (planId: number, data: CreatePricingRequest) => {
  return httpRequest.post<PlanPricing>({
    url: `${API.planPricings}/${planId}/pricings`,
    data,
  })
}

/**
 * 更新价格方案
 *
 * @param pricingId 价格方案 ID
 * @param data 更新价格方案请求数据
 * @returns 价格方案详情响应
 */
export const updatePricingAPI = (pricingId: number, data: UpdatePricingRequest) => {
  return httpRequest.put<PlanPricing>({
    url: `${API.pricings}/${pricingId}`,
    data,
  })
}

/**
 * 删除价格方案
 *
 * @param pricingId 价格方案 ID
 * @returns 删除响应
 */
export const deletePricingAPI = (pricingId: number) => {
  return httpRequest.delete<DeletePricingResponseData>({
    url: `${API.pricings}/${pricingId}`,
  })
}

/**
 * 检查用户购买限制
 *
 * @param pricingId 价格方案 ID
 * @param userId 用户 ID
 * @returns 购买限制检查响应
 */
export const checkPurchaseLimitAPI = (pricingId: number, userId: number) => {
  return httpRequest.get<CheckPurchaseLimitResponseData>({
    url: `${API.pricings}/${pricingId}/check-purchase-limit`,
    params: { user_id: userId },
  })
}

